/**
 * 原生 fetch 客户端 ( multipart / blob 下载，不走 axios )
 */

import { HttpError, UnauthorizedError } from '../http-error.js'
import { ContentDispositionUtils, DownloadUtils } from '../download-utils.js'
import { isYtbApiUrl, notifyYtbUnauthorized } from '@/views/corp/ytb/lib/ytb-access.js'
import { withPublicPath } from '../public-path.js'

export class FetchUtils {
  /** 站内 /api 绝对路径挂上部署 publicPath */
  static resolveUrl(url) {
    if (typeof url === 'string' && url.startsWith('/api')) {
      return withPublicPath(url)
    }
    return url
  }

  /**
   * JSON 请求：401 抛 UnauthorizedError，非 2xx 抛 HttpError
   * @param {string} url
   * @param {RequestInit} [init]
   */
  static async requestJson(url, init = {}) {
    const res = await fetch(FetchUtils.resolveUrl(url), init)
    FetchUtils.throwIfUnauthorized(res, url)
    const data = await FetchUtils.readJsonBody(res)
    if (!res.ok) throw HttpError.fromPayload(data, res.status)
    return data
  }

  /**
   * 主站统一 JSON 请求：校验 { code, success, message, data }
   * @param {string} url
   * @param {RequestInit} [init]
   */
  static async requestAppJson(url, init = {}) {
    const data = await FetchUtils.requestJson(url, init)
    if (!data || typeof data !== 'object' || !('code' in data)) {
      throw new HttpError('响应格式错误')
    }
    if (data.code !== 200 || data.success === false) {
      throw HttpError.fromPayload(data, data.code || 400)
    }
    return data
  }

  /**
   * 主站统一 JSON 请求，只返回 data 字段
   * @param {string} url
   * @param {RequestInit} [init]
   */
  static async requestAppData(url, init = {}) {
    const result = await FetchUtils.requestAppJson(url, init)
    return result.data || {}
  }

  /**
   * 请求二进制响应并触发下载；若 Content-Type 为 JSON 则视为业务错误
   * @param {string} url
   * @param {RequestInit} [init]
   * @param {string} [fallbackFilename]
   */
  static async requestDownload(url, init = {}, fallbackFilename = 'download') {
    const res = await fetch(FetchUtils.resolveUrl(url), init)
    FetchUtils.throwIfUnauthorized(res, url)
    const contentType = res.headers.get('Content-Type') || ''
    if (contentType.includes('application/json')) {
      const data = await FetchUtils.readJsonBody(res)
      throw HttpError.fromPayload(data, res.status, '下载失败')
    }
    if (!res.ok) throw new HttpError(`请求失败 (${res.status})`, res.status)
    const blob = await res.blob()
    const filename = ContentDispositionUtils.parseFilename(
      res.headers.get('Content-Disposition'),
      fallbackFilename,
    )
    DownloadUtils.saveAs(blob, filename)
  }

  /** @param {Response} res @param {string} [url] */
  static throwIfUnauthorized(res, url = '') {
    if (res.status === 401) {
      const reqUrl = url || res.url || ''
      if (isYtbApiUrl(reqUrl)) {
        notifyYtbUnauthorized()
      }
      throw new UnauthorizedError()
    }
  }

  /** @param {Response} res */
  static async readJsonBody(res) {
    return res.json().catch(() => ({}))
  }
}
